import { FormEvent, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useToast } from '../../../utils/useToast';

const inputClassName =
  'block w-full rounded-md border-0 py-1.5 text-white-900 shadow-sm ring-1 ring-inset ring-white-300 placeholder:text-white-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6';

type ClerkError = { errors?: { longMessage?: string; message?: string }[] };

/** Changes the signed-in user's password through Clerk. */
export function ChangePasswordForm() {
  const { user } = useUser();
  const { toast } = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    // Without this the browser submits the form natively, as a GET with the
    // passwords in the query string.
    event.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (!user) {
      setError('You need to be signed in to change your password.');
      return;
    }

    setIsSaving(true);
    try {
      await user.updatePassword({ currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast({ variant: 'success', title: 'Password updated.' });
    } catch (caught) {
      const clerkError = (caught as ClerkError)?.errors?.[0];
      setError(
        clerkError?.longMessage ||
          clerkError?.message ||
          'Unable to update your password. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form className="md:col-span-2" method="post" onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:max-w-xl sm:grid-cols-6">
        <div className="col-span-full">
          <label
            htmlFor="current-password"
            className="text-white-900 block text-sm font-medium leading-6"
          >
            Current password
          </label>
          <div className="mt-2">
            <input
              id="current-password"
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              className={inputClassName}
            />
          </div>
        </div>

        <div className="col-span-full">
          <label
            htmlFor="new-password"
            className="text-white-900 block text-sm font-medium leading-6"
          >
            New password
          </label>
          <div className="mt-2">
            <input
              id="new-password"
              type="password"
              autoComplete="new-password"
              required
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              className={inputClassName}
            />
          </div>
        </div>

        <div className="col-span-full">
          <label
            htmlFor="confirm-password"
            className="text-white-900 block text-sm font-medium leading-6"
          >
            Confirm password
          </label>
          <div className="mt-2">
            <input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              className={inputClassName}
            />
          </div>
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-4 text-sm text-red-600">
          {error}
        </p>
      )}

      <div className="mt-8 flex">
        <button
          type="submit"
          disabled={isSaving}
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-60"
        >
          {isSaving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
}
